import { ENTITIES } from "./entities";

// ⚠️ RESOURCE NAMING GUARD (read before adding save/convert/navigation code):
//   "leads"          → the PROPERTY ANALYSIS module/collection (legacy name). Labelled
//                      "Property Analysis" everywhere in the UI.
//   "inbound_leads"  → the LEADS module (inbound leads). Labelled "Leads" in the UI.
//   "property_search"→ the PROPERTY SCREENING module.
// Never pass resource "leads" when you mean the Leads module — use "inbound_leads".
// Prefer the ANALYSIS_RESOURCE / LEADS_RESOURCE constants below over the raw strings.
export const ANALYSIS_RESOURCE = "analysis";       // Property Analysis (canonical name; physical collection is "leads")
export const LEADS_RESOURCE = "inbound_leads";     // the Leads module
export const SCREENING_RESOURCE = "property_search";

export const LEAD_STATUSES = ["New", "Analyze", "Offer Approved", "Offer Sent", "Negotiating", "Not Accepted", "Under Contract", "Converted"];

export const LOT_SPLIT_OPTIONS = ["Not Allowed", "Potentially Allowed", "Confirmed Allowed", "Application Required", "Approved", "Recorded"];

const _PROPERTY_SOURCE_VALUES = [
  // Consumer marketplaces
  "MLS", "Zillow", "Redfin", "Realtor", "Realtor.com", "Homes.com", "Movoto",
  // Land marketplaces
  "LandWatch", "Land.com", "LandSearch", "Land And Farm", "LotNetwork", "LandHub",
  // Commercial / development
  "Crexi", "LoopNet", "CoStar",
  // Distressed / auction
  "Auction.com", "Hubzu", "Xome", "Bid4Assets", "Tax Auction", "Foreclosure",
  // Owner-direct / off-market
  "Off-Market", "FSBO", "Facebook Marketplace", "Craigslist", "Driving for Dollars",
  // Investor / wholesale
  "Wholesaler", "InvestorLift", "PropStream", "New Western",
  // Government / special situations
  "County Records", "Probate", "Referral",
  "Other",
];
// Alphabetical for the picklist, with "Other" always pinned last.
export const PROPERTY_SOURCE_OPTIONS = [
  ..._PROPERTY_SOURCE_VALUES.filter((v) => v !== "Other").sort((a, b) => a.localeCompare(b)),
  "Other",
];
export const PROPERTY_TYPE_OPTIONS = ["Vacant Land", "Single Family", "Multi-Family", "Teardown", "Condo", "Townhouse", "Commercial", "Other"];

// On-market vs off-market lead detection. A lead is off-market when it came from the off-market
// channel (source_type "OFF-MARKET") or its listing status is Off-Market. MLS Number + Listing URL
// only apply to on-market leads.
const _isLeadOffMarket = (r) => {
  const ch = String(r?.source_type || "").toUpperCase().replace(/_/g, "-");
  if (ch === "OFF-MARKET") return true;
  if (ch === "ACTIVE") return false;
  return String(r?.listing_status || "").toLowerCase().replace(/\s+/g, "-") === "off-market";
};
const _isOnMarket = (r) => !_isLeadOffMarket(r);
export const isLeadOnMarket = (r) => _isOnMarket(r);

export const AUCTION_STATUS_OPTIONS = ["Scheduled", "Registration Open", "Postponed", "Cancelled", "Sold", "Redeemed"];
// Tax-auction-only fields — shown only when Property Source is "Tax Auction". Included in both
// Property Search and Analysis field sets so values persist through conversion.
const _isTaxAuction = (r) => r && r.property_source === "Tax Auction";
export const AUCTION_FIELDS = [
  { key: "auction_date", label: "Upcoming Auction Date", type: "date", showIf: _isTaxAuction },
  { key: "auction_status", label: "Auction Status", type: "select", options: AUCTION_STATUS_OPTIONS, showIf: _isTaxAuction },
  { key: "auction_min_bid", label: "Minimum / Opening Bid", type: "currency", showIf: _isTaxAuction },
  { key: "auction_platform", label: "Auction Platform / Location", type: "text", showIf: _isTaxAuction },
  { key: "auction_registration_deadline", label: "Registration / Deposit Deadline", type: "date", showIf: _isTaxAuction },
];

// Per-module pathway/status bars.
export const STATUS_PATHS = {
  property_search: ["New", "Screening", "Qualified", "Not Qualified", "Ready for Analysis", "Converted"],
  leads: ["New", "Analyze", "Offer Approved", "Offer Sent", "Negotiating", "Not Accepted", "Under Contract", "Converted"],
  projects: ["Initiated", "Acquisition", "Planning", "Design", "Preconstruction", "Construction", "Closeout", "Complete"],
  preconstruction: ["Estimating", "Bidding", "Permitting", "Contractor Selection", "Contracting", "Procurement", "Mobilization", "Ready to Build", "Converted"],
  construction: ["Site Work", "Foundation", "Framing", "Rough MEP", "Dry-In", "Exterior", "Interior", "Site & Landscape", "Punch List", "Substantial Completion", "Converted"],
  closeout: ["Final Inspections", "Corrections", "Certificate of Occupancy", "Final Punch", "Financial Closeout", "Handover", "Complete"],
  acquisition: ["Approved", "Offer Prep", "Offer Submitted", "Negotiation", "Under Contract", "Due Diligence", "Financing", "Clear to Close", "Closing", "Acquired"],
  acquisition_pipeline: ["Under Contract", "Due Diligence", "Feasibility", "Financial Review", "Approved", "Not Approved", "Acquired", "Converted"],
  planning: ["Planning Started", "Site Verification", "Land Use & Zoning", "Utilities & Site Conditions", "Development Requirements", "Planning Complete"],
  inbound_leads: ["New", "Mailer Sent", "Mailer Received", "Contacted", "Qualified", "Unqualified", "Converted"],
};

// Terminal outcomes that stop an acquisition (not normal progression stages).
export const ACQUISITION_TERMINAL = ["Rejected", "Withdrawn", "Terminated"];

export const SERVICE_OPTIONS = [
  "Kitchen Remodel", "Bathroom Remodel", "Room Addition", "ADU / Garage Conversion",
  "Full Home Renovation", "New Construction", "Outdoor / Landscaping", "Roofing",
  "Flooring", "Deck / Patio", "Commercial Buildout", "Custom / Other",
];

export const JOB_TITLES = [
  "Owner", "Co-Owner", "Founder", "President", "CEO", "Partner",
  "General Manager", "Operations Manager", "Office Manager", "Project Manager",
  "Estimator", "Sales Manager", "Sales Representative", "Foreman", "Crew Lead",
  "Office Administrator", "Bookkeeper", "Marketing Manager", "Other",
];

// Land Comparables — an independent second set of comps (land sales), shown BEFORE the Home Comparables.
export const LAND_COMP_FIELDS = [
  { key: "land_comp_1_address", label: "Land Comparable 1 Address", type: "text" },
  { key: "land_comp_1_zip", label: "Land Comparable 1 Zip", type: "text" },
  { key: "land_comp_1_city", label: "Land Comparable 1 City", type: "text" },
  { key: "land_comp_1_state", label: "Land Comparable 1 State", type: "text" },
  { key: "land_comp_1_beds", label: "Land Comparable 1 Beds", type: "number" },
  { key: "land_comp_1_baths", label: "Land Comparable 1 Baths", type: "number" },
  { key: "land_comp_1", label: "Land Comparable 1 Price", type: "number" },
  { key: "land_comp_1_sqft", label: "Land Comparable 1 Lot Size (Acre)", type: "number" },
  { key: "land_comp_1_lot", label: "Land Comparable 1 Lot Size (Sq Ft)", type: "number" },
  { key: "land_comp_1_zoning", label: "Land Comparable 1 Zoning", type: "text" },
  { key: "land_comp_1_utilities", label: "Land Comparable 1 Utilities", type: "text" },
  { key: "land_comp_1_adjustment", label: "Land Comparable 1 Adjustment", type: "text" },
  { key: "land_comp_2_address", label: "Land Comparable 2 Address", type: "text" },
  { key: "land_comp_2_zip", label: "Land Comparable 2 Zip", type: "text" },
  { key: "land_comp_2_city", label: "Land Comparable 2 City", type: "text" },
  { key: "land_comp_2_state", label: "Land Comparable 2 State", type: "text" },
  { key: "land_comp_2_beds", label: "Land Comparable 2 Beds", type: "number" },
  { key: "land_comp_2_baths", label: "Land Comparable 2 Baths", type: "number" },
  { key: "land_comp_2", label: "Land Comparable 2 Price", type: "number" },
  { key: "land_comp_2_sqft", label: "Land Comparable 2 Lot Size (Acre)", type: "number" },
  { key: "land_comp_2_lot", label: "Land Comparable 2 Lot Size (Sq Ft)", type: "number" },
  { key: "land_comp_2_zoning", label: "Land Comparable 2 Zoning", type: "text" },
  { key: "land_comp_2_utilities", label: "Land Comparable 2 Utilities", type: "text" },
  { key: "land_comp_2_adjustment", label: "Land Comparable 2 Adjustment", type: "text" },
  { key: "land_comp_3_address", label: "Land Comparable 3 Address", type: "text" },
  { key: "land_comp_3_zip", label: "Land Comparable 3 Zip", type: "text" },
  { key: "land_comp_3_city", label: "Land Comparable 3 City", type: "text" },
  { key: "land_comp_3_state", label: "Land Comparable 3 State", type: "text" },
  { key: "land_comp_3_beds", label: "Land Comparable 3 Beds", type: "number" },
  { key: "land_comp_3_baths", label: "Land Comparable 3 Baths", type: "number" },
  { key: "land_comp_3", label: "Land Comparable 3 Price", type: "number" },
  { key: "land_comp_3_sqft", label: "Land Comparable 3 Lot Size (Acre)", type: "number" },
  { key: "land_comp_3_lot", label: "Land Comparable 3 Lot Size (Sq Ft)", type: "number" },
  { key: "land_comp_3_zoning", label: "Land Comparable 3 Zoning", type: "text" },
  { key: "land_comp_3_utilities", label: "Land Comparable 3 Utilities", type: "text" },
  { key: "land_comp_3_adjustment", label: "Land Comparable 3 Adjustment", type: "text" },
  { key: "land_estimated_new_construction_arv", label: "Estimated New Construction GDV", type: "number" },
  { key: "land_living_sqft", label: "Proposed Home Size (Sq Ft)", type: "number" },
  { key: "land_proposed_beds", label: "Proposed Bedrooms", type: "number" },
  { key: "land_proposed_baths", label: "Proposed Baths", type: "number" },
  { key: "land_estimated_land_value", label: "Estimated Land Value", type: "text" },
  { key: "land_price_per_sqft", label: "Land Price / SF", type: "text" },
  { key: "land_valuation_note", label: "Valuation Method", type: "text" },
  { key: "land_comparable_sales_note", label: "Comparable Land Sales (Market)", type: "text" },
  { key: "land_estimated_property_taxes", label: "Estimated Property Taxes", type: "text" },
];
const LAND_COMP_SECTIONS = [
  { title: "Land Comparable 1", keys: ["land_comp_1_address", "land_comp_1_zip", "land_comp_1_city", "land_comp_1_state", "land_comp_1", "land_comp_1_sqft", "land_comp_1_lot"] },
  { title: "Land Comparable 2", keys: ["land_comp_2_address", "land_comp_2_zip", "land_comp_2_city", "land_comp_2_state", "land_comp_2", "land_comp_2_sqft", "land_comp_2_lot"] },
  { title: "Land Comparable 3", keys: ["land_comp_3_address", "land_comp_3_zip", "land_comp_3_city", "land_comp_3_state", "land_comp_3", "land_comp_3_sqft", "land_comp_3_lot"] },
  { title: "Land Comparables Summary", keys: ["land_estimated_land_value", "land_price_per_sqft", "land_valuation_note"] },
];

export const LEAD_FIELDS = [  { key: "first_name", label: "First Name", type: "text", required: true },
  { key: "last_name", label: "Last Name", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "mobile_phone", label: "Mobile Phone", type: "text" },
  { key: "mailing_address", label: "Mailing Address", type: "text" },
  { key: "mailing_city", label: "Mailing City", type: "text" },
  { key: "mailing_state", label: "Mailing State", type: "text" },
  { key: "mailing_zip", label: "Mailing ZIP", type: "text" },
  { key: "business_phone", label: "Business Phone", type: "text" },
  { key: "preferred_channel", label: "Preferred Channel", type: "select", options: ["Phone", "Text", "Email"] },
  { key: "company_name", label: "Company", type: "text" },
  { key: "job_title", label: "Job Title", type: "select", options: JOB_TITLES },
  { key: "services_offered", label: "Services Offered", type: "multiselect", options: SERVICE_OPTIONS },
  { key: "company_size", label: "Company Size", type: "select", options: ["2-10", "11-20", "21-50", "51-200"] },
  { key: "address", label: "Address", type: "text" },
  { key: "city", label: "City", type: "text" },
  { key: "state", label: "State", type: "text" },
  { key: "postal_code", label: "Zip Code", type: "text" },
  { key: "country", label: "Country", type: "text" },
  { key: "lead_source", label: "Lead Source", type: "select", options: ["Website Form", "Facebook Lead Ads", "Google Ads", "Referral", "Import", "Manual Entry", "Webinar"] },
  { key: "lead_status", label: "Status", type: "select", options: LEAD_STATUSES },
  { key: "temperature", label: "Temperature", type: "select", options: ["Cold", "Warm", "Hot"] },
  { key: "lead_score", label: "Lead Score", type: "number" },
  { key: "product_plan", label: "Product Plan", type: "select", options: ["Starter", "Pro", "Business", "Enterprise"] },
  { key: "product_name", label: "Product", type: "text" },
  { key: "email_consent", label: "Email Consent", type: "boolean" },
  { key: "sms_consent", label: "SMS Consent", type: "boolean" },
  { key: "phone_consent", label: "Phone Consent", type: "boolean" },
  { key: "do_not_call", label: "Do Not Call", type: "boolean" },
  { key: "do_not_email", label: "Do Not Email", type: "boolean" },
  { key: "do_not_text", label: "Do Not Text", type: "boolean" },
  // Property Analysis Summary (homebuilding) fields
  { key: "property_analysis_name", label: "Property Analysis Name", type: "text" },
  { key: "analysis_start_date", label: "Analysis Start Date", type: "date" },
  { key: "arv_market_value", label: "New Construction Market Value (GDV)", type: "number" },
  { key: "analysis_status", label: "Status", type: "select", options: ["Analysis — In Progress", "Analysis — Complete", "On Hold", "Approved", "Rejected"] },
  { key: "construction_budget", label: "Hard Construction Budget", type: "number" },
  { key: "listing_escrow_sale", label: "Listing, Escrow, Sale", type: "text" },
  { key: "max_land_purchase_price", label: "Max Land Offer Price", type: "number" },
  { key: "actual_land_offer_price", label: "Actual Land Offer Price", type: "number" },
  { key: "land_adjusted_market_value", label: "Adjusted Market Land Value", type: "number" },
  { key: "best_use", label: "Best / Highest Use", type: "text" },
  { key: "potential_lots", label: "Potential Lots / Homes", type: "number" },
  { key: "finished_lot_value", label: "Finished Lot Value / Lot", type: "number" },
  { key: "avg_home_price", label: "Avg Sale Price / Home", type: "number" },
  { key: "entitlement_costs", label: "Entitlement / Development Costs", type: "number" },
  { key: "required_profit_pct", label: "Required Profit %", type: "number" },
  { key: "land_flip_margin_pct", label: "Land Flip Margin %", type: "number" },
  { key: "opening_offer_pct", label: "Opening Offer %", type: "number" },
  { key: "construction_timeline", label: "Construction", type: "text" },
  { key: "est_net_profit", label: "Minimum Required Net Profit", type: "number" },
  { key: "soft_costs", label: "Soft Costs", type: "number" },
  { key: "construction_contingency", label: "Construction Contingency", type: "number" },
  { key: "financing_carry", label: "Financing + Carrying Costs", type: "number" },
  { key: "selling_costs", label: "Selling Costs", type: "number" },
  { key: "living_sqft", label: "Proposed Home Size (Sq Ft)", type: "number" },
  { key: "lot_split_potential", label: "Lot Split Potential", type: "select", options: LOT_SPLIT_OPTIONS },
  { key: "proposed_lots", label: "Proposed Lots", type: "number" },
  { key: "lot_size_sqft", label: "Lot Size Sq Ft", type: "number" },
  { key: "lot_size_acres", label: "Lot Size Acres", type: "number" },
  { key: "total_under_roof_sqft", label: "Total Under Roof (Sq Ft)", type: "number" },
  { key: "garage_sqft", label: "Garage (Sq Ft)", type: "number" },
  { key: "covered_patio_sqft", label: "Covered Patio / Porch (Sq Ft)", type: "number" },
  { key: "planning_permitting", label: "Planning & Permitting", type: "text" },
  // Property Details (homebuilding) fields
  { key: "property_no", label: "No.", type: "text" },
  { key: "property_source", label: "Property Source", type: "select", options: PROPERTY_SOURCE_OPTIONS },
  ...AUCTION_FIELDS,
  { key: "property_type", label: "Property Type (detail)", type: "select", options: PROPERTY_TYPE_OPTIONS },
  { key: "property_classification", label: "Property Type", type: "select", options: ["Residential", "Commercial"] },
  { key: "street_name", label: "Street", type: "text" },
  { key: "apn", label: "APN", type: "text" },
  { key: "days_on_market", label: "Days on Market", type: "number" },
  { key: "list_price", label: "List Price", type: "number" },
  { key: "zoning", label: "Zoning", type: "text" },
  { key: "zoning_district", label: "Zoning District", type: "text" },
  { key: "jurisdiction", label: "Jurisdiction", type: "text" },
  { key: "permitted_uses", label: "Permitted Uses", type: "text" },
  { key: "usage", label: "Usage", type: "textarea" },
  { key: "overlay", label: "Overlay", type: "text" },
  { key: "building_controls", label: "Building Controls", type: "text" },
  { key: "parking_requirements", label: "Parking Requirements", type: "text" },
  { key: "zoning_source", label: "Zoning Source", type: "text" },
  { key: "zoning_retrieved_date", label: "Retrieved Date", type: "date" },
  // Brought over from Property Search so all search data persists + renders on the Analysis layout
  { key: "property_name", label: "Property Name", type: "text" },
  { key: "source_detail", label: "Source Detail", type: "text" },
  { key: "listing_url", label: "Listing URL", type: "text" },
  { key: "mls_number", label: "MLS Number", type: "text" },
  { key: "street_address", label: "Street Address", type: "text" },
  { key: "county", label: "County", type: "text" },
  { key: "listing_status", label: "Listing Status", type: "select", options: ["Active", "Pending", "Contingent", "Sold", "Off-Market", "Coming Soon", "Expired", "Withdrawn"] },
  { key: "date_added", label: "Date Added", type: "date" },
  { key: "asking_price", label: "Asking Price", type: "number" },
  { key: "estimated_offer_price", label: "Estimated Offer Price", type: "number" },
  { key: "seller_motivation", label: "Seller Motivation", type: "select", options: ["Unknown", "Low", "Medium", "High", "Distressed"] },
  { key: "negotiable", label: "Negotiable", type: "boolean" },
  { key: "listing_agent_contact", label: "Agent Name", type: "text" },
  { key: "listing_agent_company", label: "Company Name", type: "text" },
  { key: "listing_agent_license", label: "License Number", type: "text" },
  { key: "listing_agent_phone", label: "Agent Phone #", type: "text" },
  { key: "listing_agent_email", label: "Agent Email Address", type: "text" },
  { key: "owner_name", label: "Current Owner", type: "text" },
  { key: "owner_mailing_address", label: "Owner Mailing Address", type: "text" },
  { key: "owner_phone", label: "Owner Phone", type: "text" },
  { key: "owner_email", label: "Owner Email", type: "text" },
  { key: "financing_type", label: "Financing Type", type: "select", options: ["Cash", "Conventional", "Hard Money", "Private Money", "Seller Financing", "Construction Loan", "Other"] },
  { key: "lot_width", label: "Lot Width", type: "number" },
  { key: "lot_depth", label: "Lot Depth", type: "number" },
  { key: "street_frontage", label: "Street Frontage", type: "text" },
  { key: "parcel_shape", label: "Parcel Shape", type: "select", options: ["Rectangular", "Square", "Irregular", "Flag", "Corner", "Pie", "L-Shaped"] },
  { key: "topography", label: "Topography", type: "select", options: ["Flat", "Gentle Slope", "Steep Slope", "Hillside", "Wooded", "Wetland", "Mixed"] },
  { key: "paved_road_access", label: "Paved Road Access", type: "boolean" },
  { key: "legal_access", label: "Legal Access", type: "boolean" },
  { key: "existing_structure", label: "Existing Structure", type: "boolean" },
  { key: "demolition_required", label: "Demolition Required", type: "boolean" },
  { key: "lot_utility_water", label: "Water", type: "yesno_other" },
  { key: "lot_utility_sewer", label: "Sewer", type: "yesno_other" },
  { key: "lot_utility_electric", label: "Electric", type: "yesno_other" },
  { key: "lot_utility_septic", label: "Septic", type: "yesno_other" },
  { key: "lot_utility_natural_gas", label: "Natural Gas", type: "yesno_other" },
  { key: "zillow_estimate", label: "Zillow Estimate", type: "number" },
  { key: "redfin_estimate", label: "Redfin Estimate", type: "number" },
  { key: "realtor_estimate", label: "Realtor.com Estimate", type: "number" },
  { key: "avg_nearby_price_sqft", label: "Average Nearby Sq Ft", type: "number" },
  { key: "zoning_parcel_apn", label: "Parcel/APN", type: "text" },
  // Property Budget Analysis (homebuilding) fields — budget + matching quote
  { key: "budget_architecture_eng_permits", label: "Architecture, Engineering, Permits", type: "number" },
  { key: "quote_architecture_eng_permits", label: "Architc, Eng, Permits Quote", type: "number" },
  { key: "budget_water_sewer_setup", label: "Water & Sewer Setup", type: "number" },
  { key: "quote_water_sewer", label: "Water & Sewer Quote", type: "number" },
  { key: "budget_land_prep_excavation", label: "Land Prep/Excavation", type: "number" },
  { key: "quote_land_excavation", label: "Land/Excavation Quote", type: "number" },
  { key: "budget_foundation", label: "Foundation", type: "number" },
  { key: "quote_foundation", label: "Foundation Quote", type: "number" },
  { key: "budget_framing_materials", label: "Framing Materials", type: "number" },
  { key: "quote_framing_materials", label: "Framing Materials Quote", type: "number" },
  { key: "budget_framing_labor", label: "Framing Labor", type: "number" },
  { key: "quote_framing_labor", label: "Framing Labor Quote", type: "number" },
  { key: "budget_windows", label: "Windows", type: "number" },
  { key: "quote_window", label: "Window Quote", type: "number" },
  { key: "budget_hvac", label: "HVAC", type: "number" },
  { key: "quote_hvac", label: "HVAC Quote", type: "number" },
  { key: "budget_plumbing", label: "Plumbing", type: "number" },
  { key: "quote_plumbing", label: "Plumbing Quote", type: "number" },
  { key: "budget_electrical", label: "Electrical", type: "number" },
  { key: "quote_electrical", label: "Electrical Quote", type: "number" },
  { key: "budget_roofing", label: "Roofing", type: "number" },
  { key: "quote_roofing", label: "Roofing Quote", type: "number" },
  { key: "budget_garage_doors", label: "Garage Doors", type: "number" },
  { key: "quote_garage_door", label: "Garage Door Quote", type: "number" },
  { key: "budget_exterior_cladding_gutters", label: "Exterior Cladding & Gutters", type: "number" },
  { key: "quote_exterior_cladding_gutters", label: "Exterior Cladding & Gutters Quote", type: "number" },
  { key: "budget_insulation", label: "Insulation", type: "number" },
  { key: "quote_insulation", label: "Insulation Quote", type: "number" },
  { key: "budget_drywall", label: "Drywall", type: "number" },
  { key: "quote_drywall", label: "Drywall Quote", type: "number" },
  { key: "budget_flooring_tile", label: "Flooring & Tile", type: "number" },
  { key: "quote_flooring_tile", label: "Flooring & Tile Quote", type: "number" },
  { key: "budget_interior_painting", label: "Interior Painting", type: "number" },
  { key: "quote_interior_painting", label: "Interior Painting Quote", type: "number" },
  { key: "budget_doors_stairs_trim", label: "Doors, Stairs, Trim", type: "number" },
  { key: "quote_doors_stairs_trim", label: "Doors, Stairs, Trim Quote", type: "number" },
  { key: "budget_light_fixtures", label: "Light Fixtures", type: "number" },
  { key: "quote_light_fixtures", label: "Light Fixtures Quote", type: "number" },
  { key: "budget_cabinets_countertops", label: "Cabinets & Countertops", type: "number" },
  { key: "quote_cabinets_countertops", label: "Cabinets & Countertops Quote", type: "number" },
  { key: "budget_appliance", label: "Appliance", type: "number" },
  { key: "quote_appliance", label: "Appliance Quote", type: "number" },
  { key: "budget_driveway", label: "Driveway", type: "number" },
  { key: "quote_driveway", label: "Driveway Quote", type: "number" },
  { key: "budget_landscaping", label: "Landscaping", type: "number" },
  { key: "quote_landscaping", label: "Landscaping Quote", type: "number" },
  { key: "budget_misc_cost", label: "Misc. Cost", type: "number" },
  // Lot Utility Assessment (homebuilding) fields
  { key: "water_available", label: "Water Available", type: "boolean" },
  { key: "water_source", label: "Water Source", type: "text" },
  { key: "sewer_available", label: "Sewer Available", type: "boolean" },
  { key: "sewer_type", label: "Sewer Type", type: "text" },
  { key: "electric_available", label: "Electric Available", type: "boolean" },
  { key: "electric_power", label: "Electric Power", type: "text" },
  { key: "septic_available", label: "Septic Available", type: "boolean" },
  { key: "septic_type", label: "Septic Type", type: "text" },
  { key: "gas_available", label: "Gas Available", type: "boolean" },
  { key: "gas_type", label: "Gas Type", type: "text" },
  { key: "internet_phone_available", label: "Internet/Phone Available", type: "boolean" },
  { key: "internet_service", label: "Internet Service", type: "text" },
  { key: "utility_pole_distance_ft", label: "Utility Pole Distance (FT)", type: "number" },
  // Project Information (Projects module) fields
  { key: "pre_build_id", label: "Pre-Build ID", type: "text" },
  { key: "project_name", label: "Project Name", type: "text" },
  { key: "property_analysis", label: "Property Analysis", type: "text" },
  { key: "property_acquisition_status", label: "Property Acquisition Status", type: "select", options: ["Start Land Acquisition", "Land Under Contract", "In Escrow", "Land Acquired", "Permitting", "Under Construction", "Completed"] },
  { key: "planned_start_date", label: "Planned Start Date", type: "date" },
  { key: "planned_end_date", label: "Planned End Date", type: "date" },
  { key: "actual_start_date", label: "Actual Start Date", type: "date" },
  { key: "actual_end_date", label: "Actual End Date", type: "date" },
  { key: "project_tag", label: "Tag", type: "text" },
  ...LAND_COMP_FIELDS,
  // Home Comparables (carried over from Property Search on conversion)
  { key: "comp_1_address", label: "Comparable 1 Address", type: "text" },
  { key: "comp_1_zip", label: "Comparable 1 Zip", type: "text" },
  { key: "comp_1_city", label: "Comparable 1 City", type: "text" },
  { key: "comp_1_state", label: "Comparable 1 State", type: "text" },
  { key: "comp_1_beds", label: "Comparable 1 Beds", type: "number" },
  { key: "comp_1_baths", label: "Comparable 1 Baths", type: "number" },
  { key: "comp_1", label: "Comparable 1 Price", type: "number" },
  { key: "comp_1_sqft", label: "Comparable 1 Sq Ft", type: "number" },
  { key: "comp_1_lot", label: "Comparable 1 Lot Size (Sq Ft)", type: "number" },
  { key: "comp_1_lot_acre", label: "Comparable 1 Lot Size (Acre)", type: "number" },
  { key: "comp_2_address", label: "Comparable 2 Address", type: "text" },
  { key: "comp_2_zip", label: "Comparable 2 Zip", type: "text" },
  { key: "comp_2_city", label: "Comparable 2 City", type: "text" },
  { key: "comp_2_state", label: "Comparable 2 State", type: "text" },
  { key: "comp_2_beds", label: "Comparable 2 Beds", type: "number" },
  { key: "comp_2_baths", label: "Comparable 2 Baths", type: "number" },
  { key: "comp_2", label: "Comparable 2 Price", type: "number" },
  { key: "comp_2_sqft", label: "Comparable 2 Sq Ft", type: "number" },
  { key: "comp_2_lot", label: "Comparable 2 Lot Size (Sq Ft)", type: "number" },
  { key: "comp_2_lot_acre", label: "Comparable 2 Lot Size (Acre)", type: "number" },
  { key: "comp_3_address", label: "Comparable 3 Address", type: "text" },
  { key: "comp_3_zip", label: "Comparable 3 Zip", type: "text" },
  { key: "comp_3_city", label: "Comparable 3 City", type: "text" },
  { key: "comp_3_state", label: "Comparable 3 State", type: "text" },
  { key: "comp_3_beds", label: "Comparable 3 Beds", type: "number" },
  { key: "comp_3_baths", label: "Comparable 3 Baths", type: "number" },
  { key: "comp_3", label: "Comparable 3 Price", type: "number" },
  { key: "comp_3_sqft", label: "Comparable 3 Sq Ft", type: "number" },
  { key: "comp_3_lot", label: "Comparable 3 Lot Size (Sq Ft)", type: "number" },
  { key: "comp_3_lot_acre", label: "Comparable 3 Lot Size (Acre)", type: "number" },
  { key: "estimated_new_construction_arv", label: "Estimated New Construction GDV", type: "number" },
  { key: "proposed_beds", label: "Proposed Bedrooms", type: "number" },
  { key: "proposed_baths", label: "Proposed Baths", type: "number" },
  // Site Conditions (Angela AI — carried from Property Screening)
  { key: "fire_hazard", label: "Fire Hazard", type: "text" },
  { key: "seismic_risk", label: "Seismic / Earthquake Risk", type: "text" },
  { key: "fault_zones", label: "Fault Zones", type: "text" },
  { key: "wetlands", label: "Wetlands", type: "text" },
  { key: "environmental_concerns", label: "Environmental Concerns", type: "textarea" },
  { key: "avg_slope", label: "Average Slope", type: "text" },
  // Development Potential (Angela AI — carried from Property Screening)
  { key: "est_buildable_area", label: "Estimated Buildable Area", type: "text" },
  { key: "potential_units", label: "Potential Units", type: "text" },
  { key: "infill_opportunity", label: "Infill Opportunity", type: "text" },
  { key: "vacant_land_opportunity", label: "Vacant-Land Opportunity", type: "text" },
  { key: "development_constraints", label: "Development Constraints", type: "textarea" },
  // Market Estimates (Angela AI — carried from Property Screening)
  { key: "estimated_land_value", label: "Estimated Land Value", type: "text" },
  { key: "land_price_per_sqft", label: "Land Price / SF", type: "text" },
  { key: "comparable_sales_note", label: "Comparable Sales (Market)", type: "text" },
  { key: "estimated_property_taxes", label: "Estimated Property Taxes", type: "text" },
];

// Property Search module — its own field set (independent of Property Analysis/leads).
export const PROPERTY_SEARCH_FIELDS = [
  // Property Details
  { key: "property_name", label: "Property Name", type: "text", required: true },
  { key: "lead_status", label: "Property Status", type: "select", options: STATUS_PATHS.property_search },
  { key: "property_source", label: "Property Source", type: "select", options: PROPERTY_SOURCE_OPTIONS },
  ...AUCTION_FIELDS,
  { key: "source_detail", label: "Source Detail", type: "text" },
  { key: "listing_url", label: "Listing URL", type: "text", showIf: _isOnMarket },
  { key: "mls_number", label: "MLS Number", type: "text", showIf: _isOnMarket },
  { key: "apn", label: "APN / Parcel Number", type: "text" },
  { key: "street_address", label: "Street Address", type: "text" },
  { key: "city", label: "City", type: "text" },
  { key: "state", label: "State", type: "text" },
  { key: "postal_code", label: "ZIP", type: "text" },
  { key: "county", label: "County", type: "text" },
  { key: "property_type", label: "Property Type (detail)", type: "select", options: ["Vacant Land", "Single Family", "Multi-Family", "Teardown", "Condo", "Townhouse", "Commercial", "Other"] },
  { key: "property_classification", label: "Property Type", type: "select", options: ["Residential", "Commercial"] },
  { key: "listing_status", label: "Listing Status", type: "select", options: ["Active", "Pending", "Contingent", "Sold", "Off-Market", "Coming Soon", "Expired", "Withdrawn"] },
  { key: "date_added", label: "Date Added", type: "date", showIf: _isOnMarket },
  { key: "days_on_market", label: "Days on Market", type: "number", showIf: _isOnMarket },
  // Acquisition
  { key: "asking_price", label: "Asking Price", type: "number" },
  { key: "estimated_offer_price", label: "Estimated Offer Price", type: "number" },
  { key: "seller_motivation", label: "Seller Motivation", type: "select", options: ["Unknown", "Low", "Medium", "High", "Distressed"] },
  { key: "negotiable", label: "Negotiable", type: "boolean" },
  { key: "listing_agent_contact", label: "Agent Name", type: "text" },
  { key: "listing_agent_company", label: "Company Name", type: "text" },
  { key: "listing_agent_license", label: "License Number", type: "text" },
  { key: "listing_agent_phone", label: "Agent Phone #", type: "text" },
  { key: "listing_agent_email", label: "Agent Email Address", type: "text" },
  { key: "owner_name", label: "Current Owner", type: "text" },
  { key: "owner_mailing_address", label: "Owner Mailing Address", type: "text" },
  { key: "owner_phone", label: "Owner Phone", type: "text" },
  { key: "owner_email", label: "Owner Email", type: "text" },
  // Owner Information (persists on conversion, mirrors the Lead's Owner Information)
  { key: "first_name", label: "First Name", type: "text" },
  { key: "last_name", label: "Last Name", type: "text" },
  { key: "mailing_address", label: "Mailing Address", type: "text" },
  { key: "mailing_city", label: "Mailing City", type: "text" },
  { key: "mailing_state", label: "Mailing State", type: "text" },
  { key: "mailing_zip", label: "Mailing ZIP", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "mobile_phone", label: "Phone", type: "text" },
  { key: "financing_type", label: "Financing Type", type: "select", options: ["Cash", "Conventional", "Hard Money", "Private Money", "Seller Financing", "Construction Loan", "Other"] },
  // Lot Information
  { key: "lot_size_sqft", label: "Lot Size Sq Ft", type: "number" },
  { key: "lot_size_acres", label: "Lot Size Acres", type: "number" },
  { key: "lot_width", label: "Lot Width", type: "number" },
  { key: "lot_depth", label: "Lot Depth", type: "number" },
  { key: "street_frontage", label: "Street Frontage", type: "text" },
  { key: "parcel_shape", label: "Parcel Shape", type: "select", options: ["Rectangular", "Square", "Irregular", "Flag", "Corner", "Pie", "L-Shaped"] },
  { key: "topography", label: "Topography", type: "select", options: ["Flat", "Gentle Slope", "Steep Slope", "Hillside", "Wooded", "Wetland", "Mixed"] },
  { key: "paved_road_access", label: "Paved Road Access", type: "boolean" },
  { key: "legal_access", label: "Legal Access", type: "boolean" },
  { key: "existing_structure", label: "Existing Structure", type: "boolean" },
  { key: "demolition_required", label: "Demolition Required", type: "boolean" },
  // Lot Utilities (Yes / No / Other → specify)
  { key: "lot_utility_water", label: "Water", type: "yesno_other" },
  { key: "lot_utility_sewer", label: "Sewer", type: "yesno_other" },
  { key: "lot_utility_electric", label: "Electric", type: "yesno_other" },
  { key: "lot_utility_septic", label: "Septic", type: "yesno_other" },
  { key: "lot_utility_natural_gas", label: "Natural Gas", type: "yesno_other" },
  // Market Check
  { key: "zillow_estimate", label: "Zillow Estimate", type: "number" },
  { key: "redfin_estimate", label: "Redfin Estimate", type: "number" },
  { key: "realtor_estimate", label: "Realtor.com Estimate", type: "number" },
  { key: "avg_nearby_price_sqft", label: "Average Nearby Sq Ft", type: "number" },
  { key: "estimated_new_construction_arv", label: "Estimated New Construction GDV", type: "number" },
  // Home Comparables — price avg auto-populates the GDV above; Sq Ft avg auto-populates Proposed Home Size.
  ...LAND_COMP_FIELDS,
  // Each comparable carries its own address (ZIP auto-fills city/state), bed/bath, price and living area.
  { key: "comp_1_address", label: "Comparable 1 Address", type: "text" },
  { key: "comp_1_zip", label: "Comparable 1 Zip", type: "text" },
  { key: "comp_1_city", label: "Comparable 1 City", type: "text" },
  { key: "comp_1_state", label: "Comparable 1 State", type: "text" },
  { key: "comp_1_beds", label: "Comparable 1 Beds", type: "number" },
  { key: "comp_1_baths", label: "Comparable 1 Baths", type: "number" },
  { key: "comp_1", label: "Comparable 1 Price", type: "number" },
  { key: "comp_1_sqft", label: "Comparable 1 Sq Ft", type: "number" },
  { key: "comp_1_lot", label: "Comparable 1 Lot Size (Sq Ft)", type: "number" },
  { key: "comp_1_lot_acre", label: "Comparable 1 Lot Size (Acre)", type: "number" },
  { key: "comp_2_address", label: "Comparable 2 Address", type: "text" },
  { key: "comp_2_zip", label: "Comparable 2 Zip", type: "text" },
  { key: "comp_2_city", label: "Comparable 2 City", type: "text" },
  { key: "comp_2_state", label: "Comparable 2 State", type: "text" },
  { key: "comp_2_beds", label: "Comparable 2 Beds", type: "number" },
  { key: "comp_2_baths", label: "Comparable 2 Baths", type: "number" },
  { key: "comp_2", label: "Comparable 2 Price", type: "number" },
  { key: "comp_2_sqft", label: "Comparable 2 Sq Ft", type: "number" },
  { key: "comp_2_lot", label: "Comparable 2 Lot Size (Sq Ft)", type: "number" },
  { key: "comp_2_lot_acre", label: "Comparable 2 Lot Size (Acre)", type: "number" },
  { key: "comp_3_address", label: "Comparable 3 Address", type: "text" },
  { key: "comp_3_zip", label: "Comparable 3 Zip", type: "text" },
  { key: "comp_3_city", label: "Comparable 3 City", type: "text" },
  { key: "comp_3_state", label: "Comparable 3 State", type: "text" },
  { key: "comp_3_beds", label: "Comparable 3 Beds", type: "number" },
  { key: "comp_3_baths", label: "Comparable 3 Baths", type: "number" },
  { key: "comp_3", label: "Comparable 3 Price", type: "number" },
  { key: "comp_3_sqft", label: "Comparable 3 Sq Ft", type: "number" },
  { key: "comp_3_lot", label: "Comparable 3 Lot Size (Sq Ft)", type: "number" },
  { key: "comp_3_lot_acre", label: "Comparable 3 Lot Size (Acre)", type: "number" },
  { key: "living_sqft", label: "Proposed Home Size (Sq Ft)", type: "number" },
  { key: "proposed_beds", label: "Proposed Bedrooms", type: "number" },
  { key: "proposed_baths", label: "Proposed Baths", type: "number" },
  { key: "lot_split_potential", label: "Lot Split Potential", type: "select", options: LOT_SPLIT_OPTIONS },
  { key: "proposed_lots", label: "Proposed Lots", type: "number" },
  // Zoning (populated by the Zoneomics lookup)
  { key: "zoning", label: "Zoning", type: "text" },
  { key: "zoning_district", label: "Zoning District", type: "text" },
  { key: "jurisdiction", label: "Jurisdiction", type: "text" },
  { key: "permitted_uses", label: "Permitted Uses", type: "text" },
  { key: "usage", label: "Usage", type: "textarea" },
  { key: "zoning_parcel_apn", label: "Parcel/APN", type: "text" },
  { key: "overlay", label: "Overlay", type: "text" },
  { key: "building_controls", label: "Building Controls", type: "text" },
  { key: "parking_requirements", label: "Parking Requirements", type: "text" },
  { key: "zoning_source", label: "Zoning Source", type: "text" },
  { key: "zoning_retrieved_date", label: "Retrieved Date", type: "date" },
  // Site Conditions (Angela AI — from Property Intelligence)
  { key: "fire_hazard", label: "Fire Hazard", type: "text" },
  { key: "seismic_risk", label: "Seismic / Earthquake Risk", type: "text" },
  { key: "fault_zones", label: "Fault Zones", type: "text" },
  { key: "wetlands", label: "Wetlands", type: "text" },
  { key: "environmental_concerns", label: "Environmental Concerns", type: "textarea" },
  { key: "avg_slope", label: "Average Slope", type: "text" },
  // Development Potential (Angela AI — from Property Intelligence)
  { key: "est_buildable_area", label: "Estimated Buildable Area", type: "text" },
  { key: "potential_units", label: "Potential Units", type: "text" },
  { key: "infill_opportunity", label: "Infill Opportunity", type: "text" },
  { key: "vacant_land_opportunity", label: "Vacant-Land Opportunity", type: "text" },
  { key: "development_constraints", label: "Development Constraints", type: "textarea" },
  // Market Estimates (Angela AI — from Property Intelligence)
  { key: "estimated_land_value", label: "Estimated Land Value", type: "text" },
  { key: "land_price_per_sqft", label: "Land Price / SF", type: "text" },
  { key: "comparable_sales_note", label: "Comparable Sales (Market)", type: "text" },
  { key: "estimated_property_taxes", label: "Estimated Property Taxes", type: "text" },
];


// Fields shown on MANUAL lead creation. Status defaults to "New", Product to "Arq+",
// and Temperature + Lead Score are auto-populated by Angela (AI) — so they are hidden here
// but remain on the lead page layout.
const CREATE_HIDDEN_LEAD_KEYS = ["lead_status", "temperature", "lead_score", "data_completeness_pct", "data_completeness_summary", "product_plan", "product_name", "email_consent", "sms_consent", "phone_consent", "do_not_call", "do_not_email", "do_not_text", "comp_1_address", "comp_1_zip", "comp_1_city", "comp_1_state", "comp_1_beds", "comp_1_baths", "comp_1", "comp_1_sqft", "comp_1_lot", "comp_1_lot_acre", "comp_2_address", "comp_2_zip", "comp_2_city", "comp_2_state", "comp_2_beds", "comp_2_baths", "comp_2", "comp_2_sqft", "comp_2_lot", "comp_2_lot_acre", "comp_3_address", "comp_3_zip", "comp_3_city", "comp_3_state", "comp_3_beds", "comp_3_baths", "comp_3", "comp_3_sqft", "comp_3_lot", "comp_3_lot_acre", "estimated_new_construction_arv", "proposed_beds", "proposed_baths", ...LAND_COMP_FIELDS.map((f) => f.key)];
export const CREATE_LEAD_FIELDS = [
  ...LEAD_FIELDS.filter((f) => !CREATE_HIDDEN_LEAD_KEYS.includes(f.key)),
  { key: "campaign_id", label: "Campaign", type: "select", dynamic: true },
];

// The New Project popup only collects a Project Name (stored in first_name so it
// shows as the record's name in the table and on the detail header).
export const CREATE_PROJECT_FIELDS = [
  { key: "first_name", label: "Project Name", type: "text", required: true },
  { key: "apn", label: "APN / Parcel Number", type: "text" },
  { key: "postal_code", label: "ZIP", type: "text" },
];

// Property Search create popup collects a name + APN so we can warn on duplicate parcels.
export const CREATE_PROPERTY_SEARCH_FIELDS = [
  { key: "property_name", label: "Property Name", type: "text", required: true },
  { key: "apn", label: "APN / Parcel Number", type: "text" },
];

// Top-of-funnel Leads: lightweight lead-capture layout (contact + property + source).
export const INBOUND_LEAD_FIELDS = [
  { key: "first_name", label: "First Name", type: "text" },
  { key: "last_name", label: "Last Name", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "mobile_phone", label: "Phone", type: "text" },
  { key: "lead_source", label: "Lead Source", type: "select:Cold Call|Referral|Website|Direct Mail|Skip Trace|Zillow|Redfin|Facebook|Other" },
  { key: "address", label: "Property Address", type: "text" },
  { key: "city", label: "City", type: "text" },
  { key: "state", label: "State", type: "text" },
  { key: "postal_code", label: "ZIP", type: "text" },
  { key: "mailing_address", label: "Mailing Address", type: "text" },
  { key: "mailing_city", label: "Mailing City", type: "text" },
  { key: "mailing_state", label: "Mailing State", type: "text" },
  { key: "mailing_zip", label: "Mailing ZIP", type: "text" },
  { key: "lead_status", label: "Property Status", type: "select", options: STATUS_PATHS.inbound_leads },
  { key: "notes", label: "Notes", type: "textarea" },
  { key: "property_name", label: "Property Name", type: "text" },
  { key: "apn", label: "APN / Parcel Number", type: "text" },
  { key: "county", label: "County", type: "text" },
  { key: "lead_score", label: "Lead Score", type: "number" },
  { key: "lead_tier", label: "Tier", type: "text" },
  { key: "data_completeness_pct", label: "Completeness %", type: "number" },
  { key: "data_completeness_summary", label: "Completeness Report", type: "text" },
  { key: "listing_status", label: "Listing Status", type: "text" },
  { key: "asking_price", label: "Current Asking Price", type: "number" },
  { key: "original_price", label: "Original Asking Price", type: "number" },
  { key: "price_reduction", label: "Price Reduction", type: "number" },
  { key: "days_on_market", label: "Days on Market", type: "number", showIf: _isOnMarket },
  { key: "last_sale_date", label: "Last Sale Date", type: "date" },
  { key: "last_sale_price", label: "Last Sale Price", type: "number" },
  { key: "lot_size_sqft", label: "Lot Size (SF)", type: "number" },
  { key: "lot_size_acres", label: "Lot Size (Acres)", type: "number" },
  { key: "zoning", label: "Zoning", type: "text" },
  { key: "general_plan", label: "General Plan", type: "text" },
  { key: "jurisdiction", label: "Jurisdiction", type: "text" },
  { key: "existing_use", label: "Existing Use", type: "text" },
  { key: "existing_structure_sqft", label: "Existing Structure (SF)", type: "number" },
  { key: "year_built", label: "Year Built", type: "number" },
  { key: "vacant_improved", label: "Vacant / Improved", type: "text" },
  { key: "potential_teardown", label: "Potential Teardown", type: "text" },
  { key: "water_status", label: "Water Status", type: "text" },
  { key: "sewer_status", label: "Sewer Status", type: "text" },
  { key: "electric_status", label: "Electricity Status", type: "text" },
  { key: "electric_provider", label: "Electric Provider", type: "text" },
  { key: "electric_cost", label: "Electric Cost", type: "text" },
  { key: "recommended_strategy", label: "Recommended Strategy", type: "text" },
  { key: "theoretical_yield", label: "Theoretical Yield", type: "text" },
  { key: "preliminary_yield", label: "Preliminary Recommended Yield", type: "text" },
  { key: "confidence", label: "Confidence", type: "text" },
  { key: "seller_motivation", label: "Seller Motivation", type: "text" },
  { key: "source_platform", label: "Source Platform", type: "text" },
  { key: "source_type", label: "Source Type", type: "text" },
  { key: "source_url", label: "Source URL", type: "text" },
  { key: "mls_number", label: "MLS Number", type: "text", showIf: _isOnMarket },
  { key: "listing_agent", label: "Listing Agent", type: "text" },
  { key: "brokerage", label: "Brokerage", type: "text" },
  { key: "first_seen", label: "First Seen", type: "text" },
  { key: "information_confidence", label: "Information Confidence", type: "text" },
  { key: "thesis_why", label: "Why It Caught Our Attention", type: "textarea" },
  { key: "thesis_build", label: "What We May Build", type: "textarea" },
  { key: "thesis_kill", label: "What Could Kill the Deal", type: "textarea" },
  { key: "thesis_verify", label: "What to Verify Next", type: "textarea" },
  { key: "next_action", label: "Recommended Next Action", type: "text" },
];

export const CREATE_INBOUND_LEAD_FIELDS = [
  { key: "property_name", label: "Project Name", type: "text", required: true },
  { key: "market_type", label: "Deal Channel", type: "select", options: ["Off-Market", "On-Market"], required: true },
  { key: "apn", label: "APN / Parcel Number", type: "text" },
  { key: "postal_code", label: "ZIP", type: "text" },
];

export const OPP_STAGES = ["Programming", "Concept", "Schematic Design", "Design Development", "Engineering", "Construction Documents", "Design Complete", "Converted"];
export const STAGE_COLORS = { Qualified: "#6366f1", "Demo Scheduled": "#8b5cf6", "Demo Completed": "#a855f7", "Trial Started": "#ec4899", "Trial Follow-Up": "#f59e0b", "Pricing Discussed": "#eab308", "Subscription Pending": "#22c55e", "Closed Won": "#16a34a", "Closed Lost": "#ef4444", Engaged: "#0ea5e9" };

export const OPP_FIELDS = [
  { key: "opportunity_name", label: "Opportunity Name", type: "text", required: true },
  { key: "company_name", label: "Company", type: "text" },
  { key: "stage", label: "Stage", type: "select", options: OPP_STAGES },
  { key: "product_plan", label: "Product Plan", type: "select", options: ["Starter", "Pro", "Business", "Enterprise"] },
  { key: "probability", label: "Probability %", type: "number" },
  { key: "expected_close_date", label: "Expected Close", type: "date" },
  { key: "competitor", label: "Competitor", type: "text" },
  { key: "next_step", label: "Next Step", type: "text" },
  { key: "product_name", label: "Product", type: "text" },
];

export const PRODUCT_FIELDS = [
  { key: "name", label: "Product Name", type: "text", required: true },
  { key: "category", label: "Category", type: "text" },
  { key: "status", label: "Status", type: "select", options: ["Active", "Draft", "Sunset"] },
  { key: "short_description", label: "Short Description", type: "textarea" },
  { key: "full_description", label: "Full Description", type: "textarea" },
  { key: "website_url", label: "Website URL", type: "text" },
  { key: "target_market", label: "Target Market", type: "text" },
  { key: "icp", label: "Ideal Customer Profile", type: "textarea" },
  { key: "demo_url", label: "Demo URL", type: "text" },
  { key: "trial_url", label: "Trial URL", type: "text" },
  { key: "monthly_revenue_goal", label: "Monthly Revenue Goal", type: "number" },
  { key: "annual_revenue_goal", label: "Annual Revenue Goal", type: "number" },
];

export const CAMPAIGN_TYPES = ["Email", "SMS", "Outbound calling", "Social media", "Paid advertising", "Referral", "Product launch", "Webinar", "Demo campaign", "Trial campaign", "Customer reactivation", "Customer retention", "Review request", "Partner outreach", "Manual outreach"];
export const CAMPAIGN_STATUSES = ["Draft", "Pending Approval", "Scheduled", "Active", "Paused", "Completed", "Archived"];

export const CAMPAIGN_FIELDS = [
  { key: "name", label: "Campaign Name", type: "text", required: true },
  { key: "campaign_type", label: "Type", type: "select", options: CAMPAIGN_TYPES },
  { key: "objective", label: "Objective", type: "textarea" },
  { key: "status", label: "Status", type: "select", options: CAMPAIGN_STATUSES },
  { key: "marketing_channel", label: "Channel", type: "text" },
  { key: "budget", label: "Budget", type: "number" },
  { key: "actual_spend", label: "Actual Spend", type: "number" },
  { key: "utm_source", label: "UTM Source", type: "text" },
  { key: "utm_campaign", label: "UTM Campaign", type: "text" },
  { key: "product_name", label: "Product", type: "text" },
];

export const APPT_FIELDS = [
  { key: "appointment_type", label: "Type", type: "select", options: ["Product Demo", "Sales Call", "Onboarding", "Support", "Discovery"] },
  { key: "lead_name", label: "Lead / Contact", type: "text", required: true },
  { key: "date", label: "Date", type: "date" },
  { key: "start_time", label: "Start Time", type: "text" },
  { key: "end_time", label: "End Time", type: "text" },
  { key: "meeting_method", label: "Meeting Method", type: "select", options: ["Video Call", "Phone", "In Person"] },
  { key: "meeting_link", label: "Meeting Link", type: "text" },
  { key: "status", label: "Status", type: "select", options: ["Requested", "Scheduled", "Confirmed", "Completed", "Rescheduled", "Cancelled", "No Show"] },
  { key: "product_name", label: "Product", type: "text" },
];

// Owner Information — the same 8 fields shown on the Lead, Screening & Analysis records.
const OWNER_INFO_KEYS = ["first_name", "last_name", "mailing_address", "mailing_city", "mailing_state", "mailing_zip", "email", "mobile_phone"];
// Listing Agent — shown (and carried through conversion) for on-market listing sources.
const LISTING_AGENT_SECTION = { title: "Listing Agent", showIf: (r) => ["Realtor.com", "Zillow", "Redfin", "Homes.com"].includes(r?.property_source), keys: ["listing_agent_contact", "listing_agent_company", "listing_agent_license", "listing_agent_phone", "listing_agent_email"] };

export const SECTIONS = {
  leads: [
    { title: "Property Details", keys: ["property_name", "lead_status", "property_source", "auction_date", "auction_status", "auction_min_bid", "auction_platform", "auction_registration_deadline", "listing_url", "mls_number", "apn", "street_address", "city", "state", "postal_code", "county", "property_classification", "date_added", "days_on_market"] },
    { title: "Owner Information", keys: OWNER_INFO_KEYS },
    LISTING_AGENT_SECTION,
    { title: "Single Property Analysis Summary", keys: ["property_analysis_name", "analysis_start_date", "arv_market_value", "analysis_status", "max_land_purchase_price", "actual_land_offer_price", "construction_budget", "soft_costs", "construction_contingency", "financing_carry", "selling_costs", "est_net_profit", "living_sqft"] },
    { title: "Lot Information", keys: ["asking_price", "lot_size_sqft", "lot_size_acres", "lot_width", "lot_depth", "street_frontage", "parcel_shape", "topography", "avg_slope", "paved_road_access", "legal_access", "existing_structure", "demolition_required", "lot_utility_water", "lot_utility_sewer", "lot_utility_septic", "lot_utility_electric"] },
    { title: "Zoning", keys: ["zoning", "zoning_district", "jurisdiction", "usage", "zoning_parcel_apn", "overlay", "building_controls", "parking_requirements", "lot_split_potential", "proposed_lots", "est_buildable_area", "potential_units", "infill_opportunity", "vacant_land_opportunity", "development_constraints", "zoning_source", "zoning_retrieved_date"] },
    ...LAND_COMP_SECTIONS,
    { title: "Home Comparable 1", keys: ["comp_1_address", "comp_1_zip", "comp_1_city", "comp_1_state", "comp_1_beds", "comp_1_baths", "comp_1", "comp_1_sqft", "comp_1_lot", "comp_1_lot_acre"] },
    { title: "Home Comparable 2", keys: ["comp_2_address", "comp_2_zip", "comp_2_city", "comp_2_state", "comp_2_beds", "comp_2_baths", "comp_2", "comp_2_sqft", "comp_2_lot", "comp_2_lot_acre"] },
    { title: "Home Comparable 3", keys: ["comp_3_address", "comp_3_zip", "comp_3_city", "comp_3_state", "comp_3_beds", "comp_3_baths", "comp_3", "comp_3_sqft", "comp_3_lot", "comp_3_lot_acre"] },
    { title: "Home Comparables Summary", keys: ["estimated_new_construction_arv", "living_sqft", "proposed_beds", "proposed_baths"] },
    { title: "Property Budget Analysis", keys: ["budget_water_sewer_setup", "quote_water_sewer", "budget_land_prep_excavation", "quote_land_excavation", "budget_foundation", "quote_foundation", "budget_framing_materials", "quote_framing_materials", "budget_framing_labor", "quote_framing_labor", "budget_windows", "quote_window", "budget_hvac", "quote_hvac", "budget_plumbing", "quote_plumbing", "budget_electrical", "quote_electrical", "budget_roofing", "quote_roofing", "budget_garage_doors", "quote_garage_door", "budget_exterior_cladding_gutters", "quote_exterior_cladding_gutters", "budget_insulation", "quote_insulation", "budget_drywall", "quote_drywall", "budget_flooring_tile", "quote_flooring_tile", "budget_interior_painting", "quote_interior_painting", "budget_doors_stairs_trim", "quote_doors_stairs_trim", "budget_light_fixtures", "quote_light_fixtures", "budget_cabinets_countertops", "quote_cabinets_countertops", "budget_appliance", "quote_appliance", "budget_driveway", "quote_driveway", "budget_landscaping", "quote_landscaping"] },
    { title: "Lot Utility Assessment", keys: ["water_available", "gas_available", "sewer_available", "septic_available", "electric_available", "utility_pole_distance_ft"] },
  ],
  opportunities: [
    { title: "Overview", keys: ["opportunity_name", "company_name", "probability", "product_plan", "product_name"] },
    { title: "Value", keys: ["estimated_value", "mrr", "acv", "expected_close_date"] },
    { title: "Sales Intel", keys: ["competitor", "current_software", "employees", "decision_maker", "pain_points", "business_needs", "next_step", "loss_reason"] },
    { title: "Attribution", keys: ["campaign_name"] },
  ],
  campaigns: [
    { title: "Overview", keys: ["name", "campaign_type", "objective", "status", "marketing_channel", "lead_source", "product_name"] },
    { title: "Budget & Performance", keys: ["budget", "actual_spend", "revenue", "leads_generated", "demos", "trials", "opportunities", "customers"] },
    { title: "Tracking", keys: ["utm_source", "utm_medium", "utm_campaign", "start_date", "end_date"] },
  ],
  trials: [
    { title: "Trial", keys: ["company_name", "trial_plan", "trial_status", "start_date", "end_date", "days_remaining", "product_name"] },
    { title: "Engagement", keys: ["onboarding_status", "usage_level", "features_used", "last_login", "conversion_likelihood"] },
  ],
  customers: [
    { title: "Account", keys: ["customer_name", "primary_contact", "pricing_plan", "subscription_status", "status", "product_name"] },
    { title: "Revenue", keys: ["mrr", "acv", "start_date", "renewal_date"] },
    { title: "Health", keys: ["health_score", "usage_level", "open_issues", "cancellation_risk", "upgrade_opportunity", "last_contact", "last_login"] },
  ],
  accounts: [
    { title: "Company", keys: ["company_name", "website", "phone", "address", "city", "state", "country", "product_name"] },
    { title: "Customer Status", keys: ["is_customer", "status", "subscription_status", "pricing_plan", "primary_contact"] },
    { title: "Revenue", keys: ["mrr", "acv", "start_date", "renewal_date"] },
    { title: "Health", keys: ["health_score", "usage_level", "open_issues", "cancellation_risk", "upgrade_opportunity", "last_contact", "last_login"] },
  ],
  contacts: [
    { title: "Contact Information", keys: ["first_name", "last_name", "email", "mobile_phone", "job_title"] },
    { title: "Company", keys: ["company_name", "product_name", "status"] },
  ],
  appointments: [
    { title: "Appointment", keys: ["appointment_type", "lead_name", "status", "outcome", "product_name"] },
    { title: "Schedule", keys: ["date", "start_time", "end_time", "timezone", "meeting_method", "meeting_link", "location", "reminder_status"] },
  ],
  tasks: [
    { title: "Task", keys: ["title", "description", "task_type", "priority", "status"] },
    { title: "Details", keys: ["due_date", "product_name"] },
  ],
};

// Top-of-funnel Leads: layout is defined after Property Search (mirrors it) — see below.

// Projects module: its own first section "Project Information", then the same
// Property Details / Budget / Utility sections as the Project Analysis (leads) page.
SECTIONS.projects = [
  { title: "Project Information", keys: ["pre_build_id", "analysis_start_date", "project_name", "planned_start_date", "property_analysis", "planned_end_date", "property_acquisition_status", "actual_start_date", "actual_end_date", "project_tag"] },
  { title: "Address Information", keys: ["property_no", "state", "street_name", "postal_code", "city", "country"] },
  ...SECTIONS.leads.slice(-2),
];

// Property Search: its own dedicated layout (Property Details, Acquisition, Lot Information, Market Check).
SECTIONS.property_search = [
  { title: "Property Details", keys: ["property_name", "lead_status", "property_source", "auction_date", "auction_status", "auction_min_bid", "auction_platform", "auction_registration_deadline", "listing_url", "mls_number", "apn", "street_address", "city", "state", "postal_code", "county", "property_classification", "date_added", "days_on_market"] },
  { title: "Owner Information", keys: OWNER_INFO_KEYS },
  LISTING_AGENT_SECTION,
  { title: "Referred By", showIf: (r) => ["Wholesaler", "Realtor"].includes(r?.property_source), keys: ["listing_agent_contact", "listing_agent_phone", "listing_agent_email"] },
  { title: "Lot Information", keys: ["asking_price", "lot_size_sqft", "lot_size_acres", "lot_width", "lot_depth", "street_frontage", "parcel_shape", "topography", "paved_road_access", "legal_access", "existing_structure", "demolition_required", "lot_utility_water", "lot_utility_sewer", "lot_utility_septic", "lot_utility_electric", "lot_utility_natural_gas"] },
  { title: "Zoning", keys: ["zoning", "zoning_district", "jurisdiction", "usage", "overlay", "building_controls", "parking_requirements", "lot_split_potential", "proposed_lots", "est_buildable_area", "potential_units", "infill_opportunity", "vacant_land_opportunity", "development_constraints", "zoning_source", "zoning_retrieved_date"] },
  ...LAND_COMP_SECTIONS,
  { title: "Home Comparable 1", keys: ["comp_1_address", "comp_1_zip", "comp_1_city", "comp_1_state", "comp_1_beds", "comp_1_baths", "comp_1", "comp_1_sqft", "comp_1_lot", "comp_1_lot_acre"] },
  { title: "Home Comparable 2", keys: ["comp_2_address", "comp_2_zip", "comp_2_city", "comp_2_state", "comp_2_beds", "comp_2_baths", "comp_2", "comp_2_sqft", "comp_2_lot", "comp_2_lot_acre"] },
  { title: "Home Comparable 3", keys: ["comp_3_address", "comp_3_zip", "comp_3_city", "comp_3_state", "comp_3_beds", "comp_3_baths", "comp_3", "comp_3_sqft", "comp_3_lot", "comp_3_lot_acre"] },
  { title: "Home Comparables Summary", keys: ["estimated_new_construction_arv", "living_sqft", "proposed_beds", "proposed_baths"] },
];

// Top-of-funnel Leads: mirror the Property Screening layout + Owner Information + Lead Score.
// Fields hidden from Lot Information ONLY on the Lead object layout (still shown on Property Search).
const LEAD_HIDDEN_LOT_KEYS = ["avg_slope", "lot_utility_septic", "lot_utility_electric", "lot_utility_natural_gas", "fire_hazard", "seismic_risk", "fault_zones", "wetlands", "environmental_concerns"];
const LEAD_HIDDEN_ZONING_KEYS = ["overlay", "building_controls", "parking_requirements", "lot_split_potential", "proposed_lots", "est_buildable_area", "potential_units", "infill_opportunity", "vacant_land_opportunity", "development_constraints", "zoning_source", "zoning_retrieved_date"];
SECTIONS.inbound_leads = [
  { title: "Property Details", keys: ["property_name", "lead_status", "property_source", "listing_url", "mls_number", "apn", "address", "city", "state", "postal_code", "county", "property_classification", "date_added", "days_on_market"] },
  { title: "Owner Information", keys: OWNER_INFO_KEYS },
  ...SECTIONS.property_search.slice(2).map((s) => {
    if (s.title === "Lot Information") return { ...s, keys: s.keys.filter((k) => !LEAD_HIDDEN_LOT_KEYS.includes(k)) };
    if (s.title === "Zoning") return { ...s, keys: s.keys.filter((k) => !LEAD_HIDDEN_ZONING_KEYS.includes(k)) };
    return s;
  }),
];

// Operations phases: reuse the Property Analysis (leads) layout.
SECTIONS.preconstruction = SECTIONS.leads;
SECTIONS.construction = SECTIONS.leads;
SECTIONS.closeout = SECTIONS.leads;
SECTIONS.directory = SECTIONS.leads;
SECTIONS.financials = SECTIONS.leads;
SECTIONS.documents = SECTIONS.leads;
SECTIONS.schedule = SECTIONS.leads;

// Acquisition — Phase 1: Approved-for-Acquisition snapshot + strategy inputs.
export const ACQUISITION_FIELDS = [
  { key: "acquisition_name", label: "Acquisition Name", type: "text" },
  { key: "property_classification", label: "Property Type", type: "select", options: ["Residential", "Commercial"] },
  { key: "acquisition_strategy", label: "Acquisition Strategy", type: "select", options: ["Ground-Up New Build", "Tear Down / Rebuild", "Hold Land", "Other"] },
  { key: "purchase_method", label: "Purchase Method", type: "select", options: ["Cash", "Land Loan", "Construction Loan", "Construction-to-Permanent", "Other"] },
  { key: "buyer_entity", label: "Buyer / Purchasing Entity", type: "text" },
  // Current (live, mutable) deal terms — drive the Current column + guardrails.
  { key: "target_purchase_price", label: "Target Purchase Price", type: "number" },
  { key: "actual_purchase_price", label: "Current Purchase Price", type: "number" },
  { key: "acquisition_costs", label: "Acquisition Costs (Closing/Title/Escrow)", type: "number" },
  { key: "estimated_close_date", label: "Estimated Closing Date", type: "date" },
  { key: "closing_date", label: "Actual Closing Date", type: "date" },
  // Snapshotted approved underwriting (read-only)
  { key: "approved_arv", label: "Approved GDV", type: "number" },
  { key: "approved_max_land_price", label: "Maximum Land Purchase Price", type: "number" },
  { key: "approved_hard_construction", label: "Approved Hard Construction Budget", type: "number" },
  { key: "approved_soft_costs", label: "Approved Soft Cost Budget", type: "number" },
  { key: "approved_contingency", label: "Approved Contingency", type: "number" },
  { key: "approved_financing_carry", label: "Approved Financing / Carry Budget", type: "number" },
  { key: "approved_selling_costs", label: "Approved Selling Cost Budget", type: "number" },
  { key: "approved_min_profit_pct", label: "Minimum Required Net Profit %", type: "number" },
  { key: "approved_min_profit_amt", label: "Minimum Required Net Profit $", type: "number" },
  { key: "approved_all_in_cost", label: "Approved All-In Project Cost", type: "number" },
  { key: "approved_cost_per_sqft", label: "Approved Cost Per Square Foot", type: "number" },
  { key: "approved_living_sqft", label: "Approved Living Area (Sq Ft)", type: "number" },
  { key: "approval_date", label: "Approval Date", type: "date" },
  { key: "approved_by", label: "Approved By", type: "text" },
  // Carried-over Analysis component field defs so the Analysis sections render
  // (and are editable) on the Acquisition record after conversion.
  ...LEAD_FIELDS.filter((f) => !["first_name", "last_name", "lead_status", "status"].includes(f.key)),
];

// Analysis components carried onto the Acquisition record (same field defs/data as the Property Analysis).
const ACQ_ANALYSIS_SECTION_TITLES = ["Property Details", "Listing Agent", "Lot Information", "Zoning", "Comparable 1", "Comparable 2", "Comparable 3", "Comparables Summary", "Property Budget Analysis", "Lot Utility Assessment"];
SECTIONS.acquisition = [
  { title: "Acquisition Setup", keys: ["acquisition_name", "acquisition_strategy", "purchase_method", "buyer_entity"] },
  { title: "Current Deal Terms", keys: ["target_purchase_price", "actual_purchase_price", "acquisition_costs", "estimated_close_date", "closing_date"] },
  { title: "Approved for Acquisition", keys: ["approved_arv", "approved_max_land_price", "approved_hard_construction", "approved_soft_costs", "approved_contingency", "approved_financing_carry", "approved_selling_costs", "approved_min_profit_pct", "approved_min_profit_amt", "approved_all_in_cost", "approved_cost_per_sqft", "approved_living_sqft", "approval_date", "approved_by"] },
  ...SECTIONS.leads.filter((s) => ACQ_ANALYSIS_SECTION_TITLES.includes(s.title)).map((s) => ({ ...s, keys: [...s.keys] })),
];

// Planning — verification/development statuses.
export const VERIFY_STATUS = ["Unknown", "Preliminary", "Ordered", "In Review", "Verified", "Issue Found"];
export const ZONING_VERIFY_STATUS = ["Unknown", "Preliminary", "Agency Confirmed", "Verified"];
const YES_NO_UNKNOWN = ["Unknown", "Yes", "No"];

// Planning — Phase 1 fields (Planning Started snapshot, Site Verification,
// Land Use & Zoning, Buildable Envelope inputs).
export const PLANNING_FIELDS = [
  // Planning Started (brought forward from Property Analysis + Acquisition)
  { key: "planning_property_address", label: "Property Address", type: "text" },
  { key: "property_classification", label: "Property Type", type: "select", options: ["Residential", "Commercial"] },
  { key: "planning_apn", label: "APN", type: "text" },
  { key: "planning_lot_size_sqft", label: "Lot Size (Sq Ft)", type: "number" },
  { key: "planning_lot_acres", label: "Lot Size (Acres)", type: "number" },
  { key: "planning_acquisition_status", label: "Acquisition Status", type: "text" },
  { key: "planning_contract_price", label: "Contract Purchase Price", type: "number" },
  { key: "due_diligence_deadline", label: "Due Diligence Deadline", type: "date" },
  { key: "scheduled_closing_date", label: "Scheduled Closing Date", type: "date" },
  { key: "planning_start_date", label: "Planning Start Date", type: "date" },
  { key: "planning_owner", label: "Planning Owner", type: "text" },
  // Site Verification
  { key: "sv_status", label: "Site Verification Status", type: "select", options: VERIFY_STATUS },
  { key: "sv_apn", label: "APN", type: "text" },
  { key: "sv_gross_lot_sqft", label: "Gross Lot Sq Ft", type: "number" },
  { key: "sv_lot_acres", label: "Lot Acres", type: "number" },
  { key: "sv_lot_width", label: "Lot Width", type: "number" },
  { key: "sv_lot_depth", label: "Lot Depth", type: "number" },
  { key: "sv_street_frontage", label: "Street Frontage", type: "number" },
  { key: "sv_parcel_shape", label: "Parcel Shape", type: "select", options: ["Rectangular", "Square", "Irregular", "Flag", "Corner", "Pie", "L-Shaped"] },
  { key: "sv_legal_access", label: "Legal Access", type: "select", options: YES_NO_UNKNOWN },
  { key: "sv_public_road_access", label: "Public Road Access", type: "boolean" },
  { key: "sv_private_road_access", label: "Private Road Access", type: "boolean" },
  { key: "sv_recorded_access_easement", label: "Recorded Access Easement", type: "boolean" },
  { key: "sv_easements", label: "Easements", type: "textarea" },
  { key: "sv_encroachments", label: "Encroachments", type: "textarea" },
  { key: "sv_survey_status", label: "Survey Status", type: "select", options: VERIFY_STATUS },
  { key: "sv_survey_date", label: "Survey Date", type: "date" },
  { key: "sv_surveyor", label: "Surveyor", type: "text" },
  { key: "sv_topography", label: "Topography", type: "select", options: ["Flat", "Gentle Slope", "Steep Slope", "Hillside", "Wooded", "Wetland", "Mixed"] },
  { key: "sv_avg_slope", label: "Average Slope", type: "text" },
  { key: "sv_existing_structures", label: "Existing Structures", type: "boolean" },
  { key: "sv_demolition_required", label: "Demolition Required", type: "boolean" },
  { key: "sv_source", label: "Source", type: "text" },
  { key: "sv_provider", label: "Provider", type: "text" },
  { key: "sv_date_ordered", label: "Date Ordered", type: "date" },
  { key: "sv_verified_date", label: "Verified Date", type: "date" },
  { key: "sv_est_cost", label: "Estimated Cost", type: "number" },
  { key: "sv_actual_cost", label: "Actual Cost", type: "number" },
  { key: "sv_notes", label: "Notes", type: "textarea" },
  // Land Use & Zoning
  { key: "lz_zoning_verification_status", label: "Zoning Verification Status", type: "select", options: ZONING_VERIFY_STATUS },
  { key: "lz_jurisdiction", label: "Jurisdiction", type: "text" },
  { key: "lz_planning_dept", label: "Planning Department", type: "text" },
  { key: "lz_zoning_code", label: "Zoning Code", type: "text" },
  { key: "lz_general_plan", label: "General Plan / Land Use Designation", type: "text" },
  { key: "lz_residential_use_allowed", label: "Residential Use Allowed", type: "select", options: YES_NO_UNKNOWN },
  { key: "lz_allowed_housing_type", label: "Allowed Housing Type", type: "text" },
  { key: "lz_min_lot_size", label: "Minimum Lot Size", type: "number" },
  { key: "lz_min_lot_width", label: "Minimum Lot Width", type: "number" },
  { key: "lz_front_setback", label: "Front Setback", type: "number" },
  { key: "lz_rear_setback", label: "Rear Setback", type: "number" },
  { key: "lz_left_side_setback", label: "Left Side Setback", type: "number" },
  { key: "lz_right_side_setback", label: "Right Side Setback", type: "number" },
  { key: "lz_corner_lot_setback", label: "Corner Lot Setback", type: "number" },
  { key: "lz_max_height", label: "Maximum Building Height", type: "number" },
  { key: "lz_max_stories", label: "Maximum Stories", type: "number" },
  { key: "lz_max_lot_coverage", label: "Maximum Lot Coverage %", type: "number" },
  { key: "lz_max_far", label: "Maximum Floor Area Ratio", type: "number" },
  { key: "lz_density_limit", label: "Density Limit", type: "text" },
  { key: "lz_parking_requirement", label: "Parking Requirement", type: "text" },
  { key: "lz_adu_allowed", label: "ADU Allowed", type: "select", options: YES_NO_UNKNOWN },
  { key: "lz_overlay_district", label: "Overlay District", type: "text" },
  { key: "lz_historic_overlay", label: "Historic Overlay", type: "boolean" },
  { key: "lz_hillside_overlay", label: "Hillside Overlay", type: "boolean" },
  { key: "lz_coastal_overlay", label: "Coastal Overlay", type: "boolean" },
  { key: "lz_hoa_ccr", label: "HOA / CC&R Restrictions", type: "textarea" },
  { key: "lz_special_restrictions", label: "Special Development Restrictions", type: "textarea" },
  // Buildable Envelope inputs (calc shown in the Buildable Envelope card)
  { key: "be_gross_lot_area", label: "Gross Lot Area", type: "number" },
  { key: "be_required_front_setback", label: "Required Front Setback", type: "number" },
  { key: "be_required_rear_setback", label: "Required Rear Setback", type: "number" },
  { key: "be_required_side_setbacks", label: "Required Side Setbacks (total)", type: "number" },
  { key: "be_easement_area", label: "Easement Area", type: "number" },
  { key: "be_restricted_area", label: "Restricted Area", type: "number" },
  { key: "be_environmental_restricted_area", label: "Environmental Restricted Area", type: "number" },
  { key: "be_max_allowed_floor_area", label: "Maximum Allowed Floor Area (override)", type: "number" },
  { key: "be_est_max_living_sqft", label: "Estimated Maximum Living Sq Ft (override)", type: "number" },
  { key: "be_max_stories", label: "Maximum Stories", type: "number" },
];

SECTIONS.planning = [
  { title: "Planning Started", keys: ["planning_property_address", "planning_apn", "planning_lot_size_sqft", "planning_lot_acres", "planning_acquisition_status", "planning_contract_price", "due_diligence_deadline", "scheduled_closing_date", "planning_start_date", "planning_owner"] },
  { title: "Site Verification", keys: ["sv_status", "sv_apn", "sv_gross_lot_sqft", "sv_lot_acres", "sv_lot_width", "sv_lot_depth", "sv_street_frontage", "sv_parcel_shape", "sv_legal_access", "sv_public_road_access", "sv_private_road_access", "sv_recorded_access_easement", "sv_easements", "sv_encroachments", "sv_survey_status", "sv_survey_date", "sv_surveyor", "sv_topography", "sv_avg_slope", "sv_existing_structures", "sv_demolition_required", "sv_source", "sv_provider", "sv_date_ordered", "sv_verified_date", "sv_est_cost", "sv_actual_cost", "sv_notes"] },
  { title: "Land Use & Zoning", keys: ["lz_zoning_verification_status", "lz_jurisdiction", "lz_planning_dept", "lz_zoning_code", "lz_general_plan", "lz_residential_use_allowed", "lz_allowed_housing_type", "lz_min_lot_size", "lz_min_lot_width", "lz_front_setback", "lz_rear_setback", "lz_left_side_setback", "lz_right_side_setback", "lz_corner_lot_setback", "lz_max_height", "lz_max_stories", "lz_max_lot_coverage", "lz_max_far", "lz_density_limit", "lz_parking_requirement", "lz_adu_allowed", "lz_overlay_district", "lz_historic_overlay", "lz_hillside_overlay", "lz_coastal_overlay", "lz_hoa_ccr", "lz_special_restrictions"] },
  { title: "Buildable Envelope Inputs", keys: ["be_gross_lot_area", "be_required_front_setback", "be_required_rear_setback", "be_required_side_setbacks", "be_easement_area", "be_restricted_area", "be_environmental_restricted_area", "be_max_allowed_floor_area", "be_est_max_living_sqft", "be_max_stories"] },
];

export const isCommercial = (r) => ["property_type", "property_classification"].some((k) => String(r?.[k] || "").trim().toLowerCase() === "commercial");
// Residential-only fields hidden on Commercial records: MLS number and every beds/baths field.
const COMMERCIAL_HIDDEN = /^(mls_number|comp_\d_beds|comp_\d_baths|proposed_beds|proposed_baths)$/;

export function getSections(resource, record, hideKeys = []) {
  const cfg = getRecordConfig(resource);
  const fieldMap = {};
  cfg.fields.forEach((f) => (fieldMap[f.key] = f));
  // product_plan is opportunity-only; never surface it on leads (incl. Additional Information).
  const isLeadLike = ["leads", "projects", "property_search", "preconstruction", "construction", "closeout", "directory", "financials", "documents", "schedule", "acquisition", "acquisition_pipeline", "planning", "inbound_leads"].includes(resource);
  const resourceHide = isLeadLike ? ["product_plan"] : [];
  const present = Object.keys(record).filter((k) => !hideKeys.includes(k) && !resourceHide.includes(k) && (typeof record[k] !== "object" || Array.isArray(record[k])));
  const used = new Set();
  let base = SECTIONS[resource] || [{ title: "Information", keys: cfg.fields.map((f) => f.key) }];

  // Sourced/imported leads surface their provenance under Marketing Attribution
  // (the standalone Sourcing Provenance card was removed). Only for sourced leads.
  if (resource === "leads" && record.source_type === "sourced") {
    base = base.map((s) => s.title === "Marketing Attribution"
      ? { ...s, keys: [...s.keys, "sourced_provider", "google_rating", "google_review_count", "phone_line_type", "email_source"] }
      : s);
  }

  const commercial = isCommercial(record);
  const sections = base
    .map((s) => {
      // Show all configured section keys (even if empty/null) so they can be filled in;
      // skip only keys whose value is a real nested object (arrays & null are allowed).
      const keys = s.keys.filter((k) => {
        const val = record[k];
        if (commercial && COMMERCIAL_HIDDEN.test(k)) return false;
        return val === null || val === undefined || typeof val !== "object" || Array.isArray(val);
      });
      // Property Type sits directly under Property Name (2-column grid → index 2).
      const pc = keys.indexOf("property_classification");
      if (s.title === "Property Details" && pc > 2 && keys[0] === "property_name") keys.splice(2, 0, ...keys.splice(pc, 1));
      keys.forEach((k) => used.add(k));
      const title = commercial ? s.title.replace(/^Home Comparable/, "Building Comparable") : s.title;
      const defOf = (k) => {
        const d = fieldMap[k] || null;
        return commercial && d?.label?.includes("Home Size") ? { ...d, label: d.label.replace("Home Size", "Building Size") } : d;
      };
      return { title, showIf: s.showIf, fields: keys.map((k) => ({ key: k, def: defOf(k) })) };
    })
    .filter((s) => s.fields.length > 0 && (!s.showIf || s.showIf(record)));

  const rest = present.filter((k) => !used.has(k));
  if (rest.length && !isLeadLike) {
    sections.push({ title: "Additional Information", fields: rest.map((k) => ({ key: k, def: fieldMap[k] || null })) });
  }
  return { sections, fieldMap };
}

function titleOf(r) {
  return r.name || r.title || r.customer_name || r.opportunity_name ||
    [r.first_name, r.last_name].filter(Boolean).join(" ") || r.company_name || r.lead_name || r.subject || "Record";
}

// Bespoke record configs
const ACQ_PIPE_FIELDS = [
  { key: "property_label", label: "Property / APN", type: "text", required: true },
  { key: "owner_first", label: "First Name", type: "text" },
  { key: "owner_last", label: "Last Name", type: "text" },
  { key: "owner_phone", label: "Phone", type: "text" },
  { key: "owner_email", label: "Email", type: "text" },
  { key: "address", label: "Street Address", type: "text" },
  { key: "zip", label: "ZIP Code", type: "text" },
  { key: "city", label: "City", type: "text" },
  { key: "state", label: "State", type: "text" },
  { key: "status", label: "Outreach Status", type: "select", options: STATUS_PATHS.acquisition_pipeline },
  { key: "outreach_method", label: "Outreach Method", type: "select", options: ["Direct Mail", "Cold Call", "Text", "Email", "Door Knock", "Agent / MLS", "Skip Trace", "Other"] },
  { key: "next_action", label: "Next Action", type: "select", options: ["Send Mailer", "Call Owner", "Send Follow-Up Letter", "Research Owner", "Verify Phone Number", "Prepare Offer", "Follow Up on Offer", "Schedule Site Visit", "Other"] },
  { key: "next_action_date", label: "Next Action Date", type: "date" },
  { key: "last_contact", label: "Last Contact", type: "date" },
  { key: "attempts", label: "Attempts", type: "number" },
  { key: "seller_response", label: "Seller Response", type: "select", options: ["No Response", "Not Interested", "Interested", "Callback Requested", "Wants Offer", "Countered"] },
  { key: "seller_motivation", label: "Seller Motivation", type: "select", options: ["Unknown", "Low", "Medium", "High"] },
  { key: "target_offer", label: "Target Offer", type: "number" },
  { key: "max_offer", label: "Maximum Offer", type: "number" },
  { key: "notes", label: "Notes", type: "textarea" },
];
SECTIONS.acquisition_pipeline = [
  // Mirror the Property Analysis record — carried over on conversion.
  ...SECTIONS.leads,
];

const BESPOKE = {
  leads: { label: "Analysis", statusKey: "lead_status", fields: LEAD_FIELDS, title: (r) => r.property_name || r.property_analysis_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.company_name || "Analysis" },
  property_search: { label: "Property Screening", statusKey: "lead_status", fields: PROPERTY_SEARCH_FIELDS, title: (r) => r.property_name || r.street_address || `${r.first_name || ""} ${r.last_name || ""}`.trim() || "Property" },
  preconstruction: { label: "Preconstruction", statusKey: "lead_status", fields: LEAD_FIELDS, title: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.company_name || "Preconstruction" },
  construction: { label: "Construction", statusKey: "lead_status", fields: LEAD_FIELDS, title: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.company_name || "Construction" },
  closeout: { label: "Closeout", statusKey: "lead_status", fields: LEAD_FIELDS, title: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.company_name || "Closeout" },
  directory: { label: "Directory", statusKey: "lead_status", fields: LEAD_FIELDS, title: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.company_name || "Directory" },
  financials: { label: "Financials", statusKey: "lead_status", fields: LEAD_FIELDS, title: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.company_name || "Financials" },
  documents: { label: "Documents", statusKey: "lead_status", fields: LEAD_FIELDS, title: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.company_name || "Documents" },
  schedule: { label: "Schedule", statusKey: "lead_status", fields: LEAD_FIELDS, title: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.company_name || "Schedule" },
  projects: { label: "Project", statusKey: "lead_status", fields: LEAD_FIELDS, title: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.company_name || "Project" },
  acquisition: { label: "Acquisition", statusKey: "lead_status", fields: ACQUISITION_FIELDS, title: (r) => r.acquisition_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || "Acquisition" },
  acquisition_pipeline: { label: "Acquisition", statusKey: "status", fields: [...ACQ_PIPE_FIELDS, ...LEAD_FIELDS], title: (r) => r.property_label || `${r.owner_first || ""} ${r.owner_last || ""}`.trim() || "Acquisition" },
  inbound_leads: { label: "Lead", statusKey: "lead_status", fields: [...PROPERTY_SEARCH_FIELDS, ...INBOUND_LEAD_FIELDS], title: (r) => r.property_name || r.property_analysis_name || r.project_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.street_address || r.address || r.email || "Lead" },
  planning: { label: "Planning", statusKey: "lead_status", fields: PLANNING_FIELDS, title: (r) => r.planning_name || r.planning_property_address || r.planning_apn || "Planning" },
  opportunities: { label: "Design", statusKey: "stage", fields: OPP_FIELDS, title: (r) => r.opportunity_name },
  products: { label: "Product", statusKey: "status", fields: PRODUCT_FIELDS, title: (r) => r.name },
  campaigns: { label: "Campaign", statusKey: "status", fields: CAMPAIGN_FIELDS, title: (r) => r.name },
  appointments: { label: "Appointment", statusKey: "status", fields: APPT_FIELDS, title: (r) => `${r.appointment_type || "Appointment"} — ${r.lead_name || ""}` },
};

export function getRecordConfig(resource) {
  if (BESPOKE[resource]) return BESPOKE[resource];
  const e = ENTITIES[resource];
  if (e) return { label: e.singular, statusKey: e.statusKey || "status", fields: e.fields, title: titleOf };
  return { label: "Record", statusKey: "status", fields: [], title: titleOf };
}

// Single source of truth for the Integration Mapping (PR / REAPI) pages: the ordered list of
// Lead fields, grouped exactly as they appear on the Lead (inbound_leads) record layout.
// Because it is derived from SECTIONS.inbound_leads, ANY add / remove / relocate of a field on
// the Lead layout automatically flows through to both mapping pages — no separate list to update.
const LEAD_GIS_KEYS = new Set(["apn", "lot_size_sqft", "lot_size_acres"]);
export function getLeadLayoutFields() {
  const cfg = getRecordConfig("inbound_leads");
  const fieldMap = {};
  (cfg.fields || []).forEach((f) => { if (!(f.key in fieldMap)) fieldMap[f.key] = f; });
  const out = [];
  const seen = new Set();
  (SECTIONS.inbound_leads || []).forEach((sec) => {
    (sec.keys || []).forEach((k) => {
      if (seen.has(k)) return;
      seen.add(k);
      const def = fieldMap[k];
      out.push({ key: k, label: (def && def.label) || k, group: sec.title, gis: LEAD_GIS_KEYS.has(k) });
    });
  });
  return out;
}
